// utils/cookies.ts
const DEFAULT_PATH = '/';
export const isBrowser = typeof window !== 'undefined';

export function getRootDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const hostname = window.location.hostname;
  const ENV_DOMAIN = process.env.NEXT_PUBLIC_DOMAIN; // e.g., "ahamove.com" or "localhost"

  // If in dev and current host matches configured domain → don't override
  if (ENV_DOMAIN && hostname === ENV_DOMAIN) {
    return undefined; // use current domain implicitly
  }

  // If configured domain exists and does NOT match current host
  if (ENV_DOMAIN) {
    return `.${ENV_DOMAIN}`;
  }

  // Fallback: auto-parse root domain
  const parts = hostname.split('.');
  return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : undefined;
}

export type RawSetCookieOptions = {
  expires?: Date;
  maxAge?: number; // in seconds
  domain?: string;
};

export function setCookie(
  name: string,
  value: string,
  options: RawSetCookieOptions = {}
) {
  if (!isBrowser) return;

  const domain = options.domain ?? getRootDomain(); // May return undefined
  const cookieParts = [
    `${name}=${encodeURIComponent(value)}`,
    `path=${DEFAULT_PATH}`,
    `SameSite=Lax`,
  ];

  if (options.expires) {
    cookieParts.push(`expires=${options.expires.toUTCString()}`);
  } else if (options.maxAge) {
    const expires = new Date(Date.now() + options.maxAge * 1000);
    cookieParts.push(`expires=${expires.toUTCString()}`);
  }

  // Only add domain if valid (skip for localhost, 127.0.0.1, etc.)
  if (domain) {
    cookieParts.push(`domain=${domain}`);
  }

  document.cookie = cookieParts.join('; ');
}

export function getCookie(name: string): string | null {
  if (!isBrowser) return null;

  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(name + '='))
      ?.split('=')[1] ?? null
  );
}

export function deleteCookie(name: string, domain?: string) {
  if (!isBrowser) return;

  const rootDomain = domain ?? getRootDomain();
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${DEFAULT_PATH};`;

  if (rootDomain) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${DEFAULT_PATH}; domain=${rootDomain};`;
  }
}

export function normalizeAuthCookies() {
  if (!isBrowser) return;

  const token = getCookie('token');
  const refreshToken = getCookie('refresh_token');

  if (token) {
    setCookie('token', token);
    deleteCookie('token', window.location.hostname);
  }

  if (refreshToken) {
    setCookie('refresh_token', refreshToken);
    deleteCookie('refresh_token', window.location.hostname);
  }
}
